import { IncidentRosterPerson } from '../Types/Incident';
import { PersonType } from '../Types/enums';

const UNKNOWN_LABEL = 'Unknown tenant company';

/**
 * Derive the display name for a tenant person's company.
 * Prefers tenantName, falls back to companyName, then "Unknown tenant company".
 */
export const getTenantCompanyDisplayName = (person: IncidentRosterPerson): string => {
  const raw = (person.tenantName ?? person.companyName ?? '').trim().replace(/\s+/g, ' ');
  return raw || UNKNOWN_LABEL;
};

/**
 * Derive a normalized key for grouping tenant companies.
 * Case-insensitive, trimmed, collapsed whitespace.
 */
export const getTenantCompanyKey = (person: IncidentRosterPerson): string => {
  const raw = (person.tenantName ?? person.companyName ?? '').trim().replace(/\s+/g, ' ');
  return raw ? raw.toLowerCase() : UNKNOWN_LABEL.toLowerCase();
};

export interface TenantCompanyOption {
  key: string;
  label: string;
  count: number;
  selected: boolean;
}

/**
 * Build tenant company options from a list of people.
 * Only considers people with personType === 'tenant'.
 */
export const buildTenantCompanyOptions = (
  people: IncidentRosterPerson[],
  selectedTenantCompanies: string[],
): TenantCompanyOption[] => {
  const map = new Map<string, { label: string; count: number }>();

  for (const p of people) {
    const pt = (p.personType ?? '').toLowerCase().trim();
    if (pt !== PersonType.Tenant) continue;
    const key = getTenantCompanyKey(p);
    const existing = map.get(key);
    if (existing) {
      existing.count++;
    } else {
      map.set(key, { label: getTenantCompanyDisplayName(p), count: 1 });
    }
  }

  const unknownKey = UNKNOWN_LABEL.toLowerCase();
  const options = Array.from(map.entries()).map(([key, { label, count }]) => ({
    key,
    label,
    count,
    selected: selectedTenantCompanies.includes(key),
  }));

  // Sort: selected first, then alphabetical, "Unknown" last
  options.sort((a, b) => {
    if (a.selected !== b.selected) return a.selected ? -1 : 1;
    if (a.key === unknownKey) return 1;
    if (b.key === unknownKey) return -1;
    return a.label.localeCompare(b.label);
  });

  return options;
};

/**
 * Filter people by selected tenant companies.
 * If no tenant companies are selected, all people pass.
 * Only tenant people are filtered by company; non-tenant people always pass.
 */
export const applyTenantCompanyFilter = (
  people: IncidentRosterPerson[],
  selectedTenantCompanies: string[],
): IncidentRosterPerson[] => {
  if (selectedTenantCompanies.length === 0) return people;
  return people.filter((p) => {
    const pt = (p.personType ?? '').toLowerCase().trim();
    if (pt !== PersonType.Tenant) return true;
    return selectedTenantCompanies.includes(getTenantCompanyKey(p));
  });
};

/**
 * Format a tenant company chip label with "Company:" prefix.
 */
export const formatTenantCompanyChipLabel = (companyLabel: string): string => {
  return `Company: ${companyLabel}`;
};
